import React from 'react'

import {
  type MarkerColorScheme,
  badgeStyles,
  badgeTextStyles,
  circleStyles,
  ringStyles,
  shortNameStyles,
} from './FacilityMapMarker.styles'

export type FacilityMapMarkerProps = {
  /** Facility short name rendered inside the circle. */
  shortName: string
  /** Open incidents at this facility. Above zero switches to the alert look. */
  incidentCount?: number
  /**
   * Set false to suppress the exclamation and count badges, leaving the circle
   * and its alert coloring. For surfaces already scoped to a single incident,
   * where no facility-wide count is available to show.
   */
  showIncidentBadges?: boolean
  /** Marker diameter in px. Badges and type scale from it. */
  size?: number
  colorScheme?: MarkerColorScheme
  onClick?: () => void
  className?: string
}

const COUNT_CEILING = 999

/**
 * The facility location marker used on the Global Map and in the Incident
 * Drawer Map Panel. Positioning is the caller's job — wrap it in whatever
 * <Marker> the surrounding map provides.
 *
 * Styled with plain elements and inline styles rather than Chakra style props
 * on purpose: the Global Map is a Chakra v1 page with no DarkProvider above
 * it, and `tokenVar` emits each token's literal value as the CSS var fallback,
 * so the same component renders correctly with or without the v3 provider.
 */
export const FacilityMapMarker = ({
  shortName,
  incidentCount = 0,
  showIncidentBadges = true,
  size = 50,
  colorScheme = 'dark',
  onClick,
  className,
}: FacilityMapMarkerProps) => {
  const hasAlerts = incidentCount > 0
  const displayCount = incidentCount > COUNT_CEILING ? '1k+' : incidentCount
  const styleArgs = { size, hasAlerts, colorScheme }
  // The badges are white circles, so their glyph takes the alert color.
  const alertColor = badgeTextStyles({ size, colorScheme }).color

  return (
    <div
      className={className}
      // The alert state is carried by a CSS var with a literal fallback, which
      // jsdom drops rather than parses — so the rendered colors are not
      // assertable. This attribute is the state's testable surface.
      data-has-alerts={hasAlerts}
      data-testid='FacilityMapMarker:circle'
      onClick={onClick}
      style={circleStyles({ ...styleArgs, isInteractive: Boolean(onClick) })}
    >
      <span style={ringStyles(styleArgs)} />
      <span
        data-testid='FacilityMapMarker:shortName'
        style={shortNameStyles(styleArgs)}
      >
        {shortName}
      </span>
      {hasAlerts && showIncidentBadges && (
        <>
          <span style={badgeStyles({ size, edge: 'left' })}>
            <svg
              aria-hidden='true'
              fill='none'
              height={size * 0.36}
              role='img'
              viewBox='0 0 24 24'
              width={size * 0.36}
            >
              <path
                d='M12 4.5v10'
                stroke={alertColor}
                strokeLinecap='round'
                strokeWidth={3}
              />
              <path
                d='M12 19v.5'
                stroke={alertColor}
                strokeLinecap='round'
                strokeWidth={3}
              />
            </svg>
          </span>
          <span style={badgeStyles({ size, edge: 'right' })}>
            <span
              data-testid='FacilityMapMarker:countBadge'
              style={badgeTextStyles({ size, colorScheme })}
            >
              {displayCount}
            </span>
          </span>
        </>
      )}
    </div>
  )
}
